import { useCallback, useState } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import type { TabsProps } from './Tabs.types';

const tabListVariants = cva('flex overflow-x-auto', {
  variants: {
    variant: {
      underline: 'border-b border-neutral-200 dark:border-neutral-700',
      pills: 'gap-1',
    },
  },
  defaultVariants: {
    variant: 'underline',
  },
});

const tabTriggerVariants = cva(
  [
    'inline-flex shrink-0 items-center gap-2 whitespace-nowrap font-medium transition-colors duration-200',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
    'disabled:pointer-events-none disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        underline: '',
        pills: 'rounded-md',
      },
      size: {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-sm',
        lg: 'px-5 py-2.5 text-base',
      },
      active: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'underline',
        active: true,
        className:
          'border-b-2 border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400',
      },
      {
        variant: 'underline',
        active: false,
        className:
          'border-b-2 border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200',
      },
      {
        variant: 'pills',
        active: true,
        className:
          'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300',
      },
      {
        variant: 'pills',
        active: false,
        className:
          'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200',
      },
    ],
    defaultVariants: {
      variant: 'underline',
      size: 'md',
      active: false,
    },
  },
);

export function Tabs({
  tabs,
  activeTab: controlledActive,
  defaultTab,
  onTabChange,
  variant = 'underline',
  size = 'md',
  className,
}: TabsProps) {
  const [internalActive, setInternalActive] = useState(
    defaultTab ?? tabs[0]?.id,
  );

  const activeId = controlledActive ?? internalActive;

  const handleTabClick = useCallback(
    (tabId: string) => {
      if (!controlledActive) {
        setInternalActive(tabId);
      }
      onTabChange?.(tabId);
    },
    [controlledActive, onTabChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const enabledTabs = tabs.filter((t) => !t.disabled);
      const currentIndex = enabledTabs.findIndex((t) => t.id === activeId);

      let nextIndex: number | null = null;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        nextIndex = (currentIndex + 1) % enabledTabs.length;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        nextIndex =
          (currentIndex - 1 + enabledTabs.length) % enabledTabs.length;
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextIndex = enabledTabs.length - 1;
      }

      if (nextIndex !== null) {
        const nextTab = enabledTabs[nextIndex];
        handleTabClick(nextTab.id);
        const el = document.getElementById(`tab-${nextTab.id}`);
        el?.focus();
      }
    },
    [tabs, activeId, handleTabClick],
  );

  const activeContent = tabs.find((t) => t.id === activeId)?.content;

  return (
    <div className={className}>
      <div
        role="tablist"
        className={cn(tabListVariants({ variant }))}
        onKeyDown={handleKeyDown}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            id={`tab-${tab.id}`}
            role="tab"
            type="button"
            aria-selected={tab.id === activeId}
            aria-controls={`tabpanel-${tab.id}`}
            tabIndex={tab.id === activeId ? 0 : -1}
            disabled={tab.disabled}
            className={cn(
              tabTriggerVariants({
                variant,
                size,
                active: tab.id === activeId,
              }),
            )}
            onClick={() => handleTabClick(tab.id)}
          >
            {tab.icon && <span aria-hidden="true">{tab.icon}</span>}
            {tab.label}
          </button>
        ))}
      </div>
      <div
        id={`tabpanel-${activeId}`}
        role="tabpanel"
        aria-labelledby={`tab-${activeId}`}
        tabIndex={0}
        className="mt-4"
      >
        {activeContent}
      </div>
    </div>
  );
}
